/**
 * The headline offer sentence, as a clickable route to registration/checkout.
 *
 * Copy comes from `@/config/pricing` so every surface says the same thing, and
 * every render fires an impression once it actually enters the viewport plus a
 * click event, so the offer's conversion impact is measurable.
 */
import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { analytics } from "@/lib/analytics";
import { OFFER_COUPON_CODE, OFFER_HEADLINE, OFFER_IS_FLASH } from "@/config/pricing";

export default function OfferHeadlineLink({
  surface,
  className,
  to = "/payment",
  showCue = true,
}: {
  /** Where this instance lives — "hero", "pricing", "checkout", "offer_bar". */
  surface: string;
  className?: string;
  to?: string;
  showCue?: boolean;
}) {
  const ref = useRef<HTMLAnchorElement | null>(null);
  const seen = useRef(false);

  useEffect(() => {
    if (!OFFER_IS_FLASH) return;
    const node = ref.current;
    if (!node || typeof IntersectionObserver === "undefined") return;

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting && !seen.current) {
            seen.current = true;
            analytics.offerImpression({ surface, coupon: OFFER_COUPON_CODE });
            observer.disconnect();
          }
        }
      },
      { threshold: 0.5 },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [surface]);

  if (!OFFER_IS_FLASH) return null;

  return (
    <Link
      ref={ref}
      to={to}
      onClick={() =>
        analytics.offerClick({ surface, coupon: OFFER_COUPON_CODE, destination: to })
      }
      aria-label={`${OFFER_HEADLINE} Register for the assessment.`}
      className={cn(
        "group inline-flex min-h-[44px] items-center gap-2 rounded-2xl text-left underline-offset-4 transition-opacity hover:underline hover:opacity-95",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        className,
      )}
    >
      <span>
        {OFFER_HEADLINE}
        {showCue && (
          <span className="ml-1.5 whitespace-nowrap font-extrabold">
            Register now
            <ArrowRight
              className="ml-1 inline h-4 w-4 transition-transform group-hover:translate-x-0.5"
              aria-hidden="true"
            />
          </span>
        )}
      </span>
    </Link>
  );
}
